import _ from 'lodash';
import { MenuItem, Select } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import 'react-accessible-accordion/dist/fancy-example.css';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Accordion, AccordionItem, AccordionItemHeading, AccordionItemButton, AccordionItemPanel } from 'react-accessible-accordion';

import PropertiesSlider from './PropertiesSlider';
import StageHistorySlider from './StageHistorySlider';
import { getPipelines } from '../../../store/Deals/index.js';
import PropertyField from '../../../components/Properties/PropertyField.js';
import { updateObject, deleteObject } from '../../../store/Objects/index.js';
import { AttachmentList, AttachmentUploader } from '../../../components/Attachments';
import { updateSinglePageField, getSinglePageData } from '../../../store/SinglePage/index.js';
import { setActiveActivityBox, createRelation, uploadAttachment } from '../../../store/Activities/index.js';

const LeftSidebar = ({ singlePageData, singleId, dropdowns, toggleDropdownAction, setRelationsModal }) => {

  const history = useHistory();
  const dispatch = useDispatch();
  const { pipelines } = useSelector(state => state['Deals']);
  const { object } = useSelector(state => state['SinglePage']);
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const { properties = {} } = singlePageData || {};

  const [pendingFiles, setPendingFiles] = useState([]);
  const [objectAttachments, setObjectAttachments] = useState((singlePageData?.['attachments'] || []).filter(attachment => !attachment.isDeleted));
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);

  const [editingProperty, setEditingProperty] = useState(null);
  const [tempPropertyValue, setTempPropertyValue] = useState('');
  const [isPropertiesSlider, setIsPropertiesSlider] = useState(false);
  const [isStageHistorySlider, setIsStageHistorySlider] = useState(false);
  const [editingField, setEditingField] = useState({ field: null, value: '' });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => { if (!pipelines || pipelines.length === 0) dispatch(getPipelines()) }, []);

  useEffect(() => {
    if (singlePageData?.['attachments']) {
      setObjectAttachments(singlePageData['attachments'].filter(attachment => !attachment.isDeleted));
    }
  }, [singlePageData]);

  const openActivity = (activityBox) => {
    // Build associations object dynamically based on current object type
    const associations = { [object['plural']]: [singleId] };
    const activityData = { activityBox, source: 'sidebar', associations };
    dispatch(setActiveActivityBox(activityData));
  }

  // Generic function for opening communication activities (SMS, Call) with proper associations
  const openCommunicationActivity = (activityBox) => {
    const objectType = object?.['name'];
    const associations = {};

    // Add current object to associations (if not a contact)
    if (objectType !== 'contact') associations[object['plural']] = [singleId];

    // Add contact IDs from related contacts (filter for those with phone numbers for SMS/call)
    if (objectType === 'contact') associations['contacts'] = [singlePageData['_id']];
    else if (singlePageData?.['contacts']?.length > 0) associations['contacts'] = singlePageData['contacts'].filter(c => c['phone']).map(c => c['_id']);

    const activityData = { activityBox, source: 'sidebar', associations };
    dispatch(setActiveActivityBox(activityData));
  }

  const handleStageChange = (stageId) => {
    // Update locally first
    dispatch(updateSinglePageField({ stage: stageId }))

    // Then update in backend
    dispatch(updateObject({ id: singleId, data: { stage: stageId }, objectType: object['plural']?.toLowerCase() || 'deals' }))
  }

  const cancelEditing = () => setEditingField({ field: null, value: '' });
  const startEditing = (field, currentValue) => setEditingField({ field, value: currentValue || '' });

  const handleAttachmentDelete = (attachmentId) => {
    // For now, just update local state
    // In a full implementation, this would dispatch an action to delete from server
    setObjectAttachments(prev => prev.filter(a => a['_id'] !== attachmentId));
  };

  const handleConfirmDelete = () => {
    dispatch(deleteObject({ objectType: object['plural']?.toLowerCase() || 'deals', id: singleId }));
    setIsDeleteModalOpen(false);
    history.push(`/objects/${object['type']}`);
  };

  const handleFilesAdded = (files) => {
    setPendingFiles(files);
  };

  const handleUploadFiles = () => {
    pendingFiles.forEach(file => {
      dispatch(uploadAttachment({
        file,
        objectType: object['name'],
        objectId: singleId,
      }));
    });

    // Close modal and clear pending files
    setAttachmentModalOpen(false);
    setPendingFiles([]);
    
  };

  const saveField = (localValue) => {
    let { field, value } = editingField;
    if (!field) return;
    if (localValue) value = localValue;

    // Special handling for title field
    if (field === 'title') {
      saveTitleField(value);
      return;
    }

    // Validate amount if editing amount field
    const finalValue = field === 'amount' ? parseFloat(value) || 0 : value;

    // Update locally first
    dispatch(updateSinglePageField({ [field]: finalValue }));

    // Then update in backend
    dispatch(updateObject({
      objectType: object['plural']?.toLowerCase() || 'deals',
      id: singleId,
      data: { [field]: finalValue },
      successCallback: () => cancelEditing()
    }));
  }

  const saveTitleField = (titleValue) => {
    const titleFields = singlePageData?.['titleFields'] || ['title'];
    let updates = {};

    if (titleFields.length === 1) {
      // Single field title (deals, companies)
      updates[titleFields[0]] = titleValue;
    } else if (titleFields.length === 2 && titleFields.includes('firstName') && titleFields.includes('lastName')) {
      // Multi-field title for contacts (firstName, lastName)
      const nameParts = titleValue.trim().split(' ');
      if (nameParts.length === 1) {
        // Only one name part provided, use it as firstName
        updates['firstName'] = nameParts[0];
        updates['lastName'] = '';
      } else {
        // Multiple parts: first part is firstName, rest is lastName
        updates['firstName'] = nameParts[0];
        updates['lastName'] = nameParts.slice(1).join(' ');
      }
    }

    // Update locally first
    dispatch(updateSinglePageField({ ...updates, displayTitle: titleValue }));

    // Then update in backend using generic updateObject
    dispatch(updateObject({
      id: singleId,
      data: updates,
      successCallback: () => cancelEditing(),
      objectType: object['plural']?.toLowerCase() || 'deals',
    }))
  }

  const handleKeyPress = (e) => {
    if (e['key'] === 'Enter') saveField();
    else if (e['key'] === 'Escape') cancelEditing();
  }

  // Handle removing a relation between objects
  const handleRemoveRelation = (targetId, targetType) => {
    const sourceType = object['name']; // 'deal', 'contact', etc.
    const relationType = `${sourceType}_${targetType}`;
    dispatch(createRelation({ relationType, sourceId: singleId, targetId, action: 'remove' }));
  }

  // Get properties that should be shown in About section
  const getAboutProperties = () => {
    const aboutProperties = [];
    for (const group of Object.values(properties)) {
      if (Array.isArray(group)) {
        const propsToShow = group.filter(prop => prop['displayConfig']?.['showInAbout'] === true)
        aboutProperties.push(...propsToShow);
      }
    }
    return aboutProperties.sort((a, b) => (a['order'] || 1000) - (b['order'] || 1000));
  }

  // Get properties that should be shown in list view (main sidebar)
  const getOverviewProperties = () => {
    const overviewProperties = [];
    for (const group of Object.values(properties)) {
      if (Array.isArray(group)) {
        const propsToShow = group.filter(prop => prop['displayConfig']?.['showInList'] === true && prop['name'] !== 'title') // Title is handled separately
        overviewProperties.push(...propsToShow);
      }
    }
    return overviewProperties.sort((a, b) => (a['order'] || 1000) - (b['order'] || 1000));
  }

  // Get property configuration from store
  const getPropertyConfig = (propertyKey) => {
    for (const group of Object.values(properties)) {
      const prop = group.find(p => p['name'] === propertyKey);
      if (prop) return prop;
    }
    return null;
  }

  // Get property value
  const getPropertyValue = (propertyKey) => {
    // Check if it's a custom field (isSystem: false)
    const config = getPropertyConfig(propertyKey);
    if (config && config['isSystem'] === false) {
      return singlePageData?.['customFields']?.[propertyKey];
    }
    return singlePageData?.[propertyKey] || '';
  }

  // Start editing property
  const handleStartPropertyEdit = (propertyKey) => {
    const value = getPropertyValue(propertyKey);
    setEditingProperty(propertyKey);
    setTempPropertyValue(value || '');
  }

  // Save property
  const handleSaveProperty = async (value) => {
    if (!editingProperty) return;

    // Check if it's a custom field
    const config = getPropertyConfig(editingProperty);
    const finalValue = value || tempPropertyValue;

    let updateData;
    if (config && config['isSystem'] === false) {
      // For custom fields, update within customFields object
      updateData = {
        customFields: {
          ...singlePageData?.['customFields'],
          [editingProperty]: finalValue
        }
      };
    } else {
      // For system fields, update at root level
      updateData = { [editingProperty]: finalValue };
    }

    // Update locally first
    dispatch(updateSinglePageField(updateData));

    // Then update in backend
    dispatch(updateObject({
      objectType: object['plural']?.toLowerCase() || 'deals',
      id: singleId,
      data: updateData,
      successCallback: () => {
        setEditingProperty(null);
        setTempPropertyValue('');
      }
    }))
  }

  // Cancel property editing
  const handleCancelPropertyEdit = () => {
    setEditingProperty(null);
    setTempPropertyValue('');
  }

  // Render property based on type
  const renderPropertyField = (propertyKey) => {
    const value = getPropertyValue(propertyKey);
    const config = getPropertyConfig(propertyKey);
    if (!config) return null;
    return (
      <PropertyField
        library='mui'
        mode='display'
        value={value}
        property={config}
        objectType={object['plural']?.toLowerCase() || 'deals'}
        objectId={singleId}
        editable={!config['displayConfig']?.['readOnly']}
        onStartEdit={() => handleStartPropertyEdit(propertyKey)}
        onChange={(fieldName, newValue) => {
          // Prepare update data based on whether it's a custom field
          let updateData;
          if (config['isSystem'] === false) {
            // For custom fields, update within customFields object
            updateData = {
              customFields: {
                ...singlePageData?.['customFields'],
                [fieldName]: newValue
              }
            };
          } else {
            // For system fields, update at root level
            updateData = { [fieldName]: newValue };
          }

          // Update local state immediately
          dispatch(updateSinglePageField(updateData));

          // For multiselect fields (including tags), only update local state.
          // Tags are saved to backend by bulkUpdateObjectTags in TagsManager.
          if (config['type'] === 'multiselect') return;

          const dataToSave = (config['type'] === 'user' && typeof newValue === 'object' && !Array.isArray(newValue)) ? newValue['_id'] : newValue;

          let backendData;
          if (config['isSystem'] === false) backendData = {customFields: {[fieldName]: dataToSave} };
          else backendData = { [fieldName]: dataToSave };
          dispatch(updateObject({ id: singleId, data: backendData, objectType: object['plural']?.toLowerCase() || singlePageData?.['objectType'] || 'deals' }));
        }}
        onCancel={handleCancelPropertyEdit}
        onSave={(fieldName, newValue, needRefresh) => {
          if (needRefresh) dispatch(getSinglePageData({ id: singleId, objectType: singlePageData?.['objectType'] || object['plural']?.toLowerCase() || 'deals' }));
          else handleSaveProperty(newValue);
        }}
        additionalData={{ users: currentWorkspace?.['members'] || [] }}
      />
    )
  }

  // Render all mapped properties
  const renderAboutProperties = () => {
    const aboutProperties = getAboutProperties();

    // If no properties with showInAbout, return empty message
    if (aboutProperties.length === 0) return <p className="no-properties">No properties to display</p>;

    return aboutProperties.map(prop => <div key={prop['name']} className="property-item"> {renderPropertyField(prop['name'])} </div>);
  }

  return (
    <>
      <aside className="deal-sidebar">
        <div className="sidebar-header">
          <Link className="back-button" to={`/objects/${object['type']}`}>
            <i>
              <svg width="5" height="7" viewBox="0 0 5 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.23859 6.40987C4.23859 6.25987 4.18332 6.10987 4.0649 5.99145L1.6965 3.62305L4.0649 1.25464C4.29385 1.02569 4.29385 0.646749 4.0649 0.417803C3.83596 0.188857 3.45701 0.188857 3.22807 0.417803L0.441241 3.20463C0.212295 3.43357 0.212295 3.81252 0.441241 4.04147L3.22807 6.82829C3.45701 7.05724 3.83596 7.05724 4.0649 6.82829C4.18332 6.70987 4.23859 6.55987 4.23859 6.40987Z" fill="currentColor" />
              </svg>
            </i>
            Back to {object['type']}
          </Link>
          <div className='right-siede'>
            <Dropdown isOpen={dropdowns['client'] || false} toggle={() => toggleDropdownAction('client')} >
              <DropdownToggle caret>
                Actions
                <i className="icon">
                  <svg width="5" height="4" viewBox="0 0 5 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.74219 2.40453L2.38875 0L0.385372 0C0.0425479 0 -0.128864 0.552327 0.113969 0.876105L1.96379 3.34253C2.26019 3.73773 2.74229 3.73773 3.03868 3.34253L3.74219 2.40453Z" fill="currentColor" />
                    <path d="M4.61365 0L2.38887 0L3.74231 2.40453L4.88863 0.876105C5.12789 0.552327 4.95648 0 4.61365 0Z" fill="currentColor" />
                  </svg>
                </i>
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => setIsPropertiesSlider(true)}>View all properties</DropdownItem>
                {object['name'] === 'deal' && (
                  <DropdownItem onClick={() => setIsStageHistorySlider(true)}>
                    View Stage History
                    <i className="icon-left">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14.4c-3.52 0-6.4-2.88-6.4-6.4S4.48 1.6 8 1.6s6.4 2.88 6.4 6.4-2.88 6.4-6.4 6.4zM8.4 4H7.2v4.8l4.24 2.52.56-.96-3.6-2.16V4z" fill="currentColor" />
                      </svg>
                    </i>
                  </DropdownItem>
                )}
                {/* <DropdownItem>
                  Summarize
                  <i className="icon-left">
                    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" focusable="false" data-icon-name="BreezeSingleStar" class="StyledIcon-sc-1i65roo-0 eXwSuJ"><path d="M29.62 15.12c-6.94 0-12.74-5.79-12.74-12.73 0-.48-.4-.88-.88-.88s-.88.4-.88.88c0 6.94-5.8 12.73-12.74 12.73-.49 0-.88.4-.88.88 0 .49.39.88.88.88 6.94 0 12.74 5.79 12.74 12.73 0 .48.4.88.88.88s.88-.4.88-.88c0-6.94 5.8-12.73 12.74-12.73.49 0 .88-.4.88-.88 0-.49-.39-.88-.88-.88"></path></svg>
                  </i>
                </DropdownItem> */}
                <DropdownItem onClick={() => setIsDeleteModalOpen(true)}>Delete</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
        <div className="sidebar-content">
          {/* Title - Always shown at the top */}
          {editingField['field'] === 'title' ? (
            <input
              autoFocus
              type="text"
              onBlur={() => saveField()}
              onKeyDown={handleKeyPress}
              value={editingField['value']}
              onChange={(e) => setEditingField({ ...editingField, value: e['target']['value'] })}
              style={{ fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', marginBottom: '8px', padding: '4px 8px' }}
            />
          ) : (
            <h4
              title="Click to edit"
              style={{ cursor: 'pointer' }}
              onClick={() => startEditing('title', singlePageData?.['displayTitle'] || singlePageData?.['title'] || 'Untitled')}
            >
              {singlePageData?.['displayTitle'] || singlePageData?.['title'] || 'Untitled'}
            </h4>
          )}

          {/* Dynamic properties with showInList: true */}
          {(() => {
            const overviewProperties = getOverviewProperties();
            return overviewProperties.map(prop => {
              // Special handling for stage field in deals
              if (prop['name'] === 'stage' && object['name'] === 'deal') {
                return (
                  <div key={prop['name']} className="deal-box-owner">
                    <p><strong>{prop['label']}:</strong> </p>
                    <Select
                      size="small"
                      variant="standard"
                      value={singlePageData?.['stage'] || ''}
                      onChange={(e) => handleStageChange(e['target']['value'])}
                      renderValue={(selected) => {
                        const currentPipeline = pipelines?.find(p => p['_id'] === singlePageData?.['pipelineId']?.['_id']);
                        const stage = currentPipeline?.['stages']?.find(s => s['id'] === selected);
                        return stage?.['name'] || _.capitalize(selected) || '-';
                      }}>
                      {(() => {
                        const currentPipeline = pipelines?.find(p => p['_id'] === singlePageData?.['pipelineId']?.['_id']);
                        return currentPipeline?.['stages']?.map(stage => (
                          <MenuItem key={stage['id']} value={stage['id']}> {stage['name']} </MenuItem>
                        )) || [];
                      })()}
                    </Select>
                  </div>
                );
              }

              // Use PropertyField component for all other properties
              return renderPropertyField(prop['name']);
            });
          })()}
          <div className='links-area'>
            <ul>
              <li>
                <button onClick={() => openActivity('note')}>
                  <i className='icon'>
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.04167 10.1666V9.70831C6.04167 7.97984 6.04167 7.11561 6.57865 6.57863C7.11559 6.04164 7.97987 6.04164 9.70834 6.04164H10.1667M4.66667 1H6.5C8.22847 1 9.09275 1 9.62969 1.53697C10.1667 2.07394 10.1667 2.93819 10.1667 4.66665V4.85342C10.1667 5.66412 10.1667 6.06947 10.0058 6.42774C9.84488 6.78602 9.54192 7.05534 8.936 7.59393L7.07971 9.24397C6.56848 9.69836 6.31287 9.9256 6.00088 10.0452L5.99148 10.0488C5.67881 10.1666 5.3368 10.1666 4.65286 10.1666C2.93781 10.1666 2.08029 10.1666 1.54467 9.63736L1.52933 9.622C1 9.08635 1 8.22886 1 6.51382V4.66665C1 2.93818 1 2.07394 1.53697 1.53697C2.07394 1 2.93818 1 4.66667 1Z" stroke="currentColor" stroke-width="0.763889" stroke-linejoin="round" />
                    </svg>
                    <div className='overlaybox-tooltip'>
                      <p>Add Note</p>
                    </div>
                  </i>
                </button>
              </li>
              <li>
                <button onClick={() => openActivity('task')}>
                  <i className='icon'>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.14655 11.5838H4.07322C2.21476 11.5838 1.14551 10.5146 1.14551 8.65609V3.56443C1.14551 1.70597 2.21476 0.636719 4.07322 0.636719H8.14655C10.005 0.636719 11.0743 1.70597 11.0743 3.56443V8.65609C11.0743 10.5146 10.005 11.5838 8.14655 11.5838ZM4.07322 1.40047C2.617 1.40047 1.90926 2.10821 1.90926 3.56443V8.65609C1.90926 10.1123 2.617 10.8201 4.07322 10.8201H8.14655C9.60277 10.8201 10.3105 10.1123 10.3105 8.65609V3.56443C10.3105 2.10821 9.60277 1.40047 8.14655 1.40047H4.07322Z" fill="currentColor" />
                      <path d="M9.41952 4.7096H8.40118C7.62725 4.7096 7.00098 4.08332 7.00098 3.30939V2.29105C7.00098 2.0823 7.17409 1.90918 7.38285 1.90918C7.59161 1.90918 7.76473 2.0823 7.76473 2.29105V3.30939C7.76473 3.66071 8.04986 3.94585 8.40118 3.94585H9.41952C9.62828 3.94585 9.80139 4.11896 9.80139 4.32772C9.80139 4.53648 9.62828 4.7096 9.41952 4.7096Z" fill="currentColor" />
                      <path d="M6.10995 7.00105H4.07328C3.86452 7.00105 3.69141 6.82794 3.69141 6.61918C3.69141 6.41042 3.86452 6.2373 4.07328 6.2373H6.10995C6.31871 6.2373 6.49182 6.41042 6.49182 6.61918C6.49182 6.82794 6.31871 7.00105 6.10995 7.00105Z" fill="currentColor" />
                      <path d="M8.14661 9.03768H4.07328C3.86452 9.03768 3.69141 8.86456 3.69141 8.6558C3.69141 8.44704 3.86452 8.27393 4.07328 8.27393H8.14661C8.35537 8.27393 8.52849 8.44704 8.52849 8.6558C8.52849 8.86456 8.35537 9.03768 8.14661 9.03768Z" fill="currentColor" />
                    </svg>
                    <div className='overlaybox-tooltip'>
                      <p>Add Task</p>
                    </div>
                  </i>
                </button>
              </li>
              {/* Show SMS icon only if: contact has phone, or other objects have contacts with phone */}
              {(object?.['name'] === 'contact' ? singlePageData?.['phone'] : singlePageData?.['contacts']?.some(c => c['phone'])) && (
              <li>
                <button onClick={() => openCommunicationActivity('sms')}>
                  <i className='icon'>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <div className='overlaybox-tooltip'>
                      <p>Send SMS</p>
                    </div>
                  </i>
                </button>
              </li>
              )}
              {/* Show call icon only if: contact has phone, or other objects have contacts with phone */}
              {(object?.['name'] === 'contact' ? singlePageData?.['phone'] : singlePageData?.['contacts']?.some(c => c['phone'])) && (
              <li>
                <button onClick={() => openCommunicationActivity('call')}>
                  <i className='icon'>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.695 9.02366C10.695 9.18891 10.6583 9.35875 10.5802 9.52399C10.5022 9.68924 10.4012 9.84531 10.2681 9.9922C10.0432 10.2401 9.7953 10.4191 9.5153 10.5338C9.23989 10.6486 8.94152 10.7083 8.62021 10.7083C8.15201 10.7083 7.65167 10.5981 7.1238 10.3732C6.59592 10.1483 6.06805 9.84531 5.54476 9.46432C5.01689 9.07874 4.51655 8.65185 4.03917 8.17906C3.56638 7.70168 3.13949 7.20134 2.7585 6.67806C2.3821 6.15477 2.07915 5.63149 1.85882 5.11279C1.63849 4.58951 1.52832 4.08917 1.52832 3.61179C1.52832 3.29966 1.5834 3.00129 1.69357 2.72588C1.80373 2.44588 1.97816 2.18882 2.22144 1.95931C2.51522 1.67013 2.83653 1.52783 3.17621 1.52783C3.30474 1.52783 3.43326 1.55537 3.54802 1.61046C3.66736 1.66554 3.77294 1.74816 3.85556 1.86751L4.92049 3.36851C5.00312 3.48327 5.06279 3.58884 5.1041 3.68983C5.14541 3.78622 5.16836 3.88261 5.16836 3.96983C5.16836 4.07999 5.13623 4.19016 5.07197 4.29573C5.0123 4.40131 4.92508 4.51147 4.81492 4.62164L4.46606 4.98427C4.41557 5.03476 4.39262 5.09443 4.39262 5.16788C4.39262 5.2046 4.39721 5.23673 4.40639 5.27345C4.42016 5.31017 4.43393 5.33771 4.44311 5.36525C4.52573 5.51673 4.66803 5.71411 4.87 5.9528C5.07656 6.19149 5.29689 6.43478 5.53558 6.67806C5.78345 6.92134 6.02214 7.14626 6.26543 7.35282C6.50412 7.55479 6.7015 7.6925 6.85756 7.77512C6.88052 7.7843 6.90806 7.79807 6.94019 7.81184C6.97691 7.82561 7.01363 7.8302 7.05494 7.8302C7.13298 7.8302 7.19265 7.80266 7.24314 7.75217L7.592 7.4079C7.70676 7.29315 7.81692 7.20593 7.9225 7.15085C8.02807 7.08659 8.13365 7.05446 8.2484 7.05446C8.33561 7.05446 8.42742 7.07282 8.5284 7.11413C8.62939 7.15544 8.73496 7.21511 8.84972 7.29315L10.3691 8.37185C10.4884 8.45447 10.5711 8.55087 10.6215 8.66562C10.6674 8.78038 10.695 8.89513 10.695 9.02366Z" stroke="currentColor" stroke-width="0.763889" stroke-miterlimit="10" />
                    </svg>
                    <div className='overlaybox-tooltip'>
                      <p>Call</p>
                    </div>
                  </i>
                </button>
              </li>
              )}
              <li>
                <button onClick={() => openActivity('mail')}>
                  <i className='icon'>
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.1667 1.03711H3.05559C1.93057 1.03711 1.01855 1.94912 1.01855 3.07415V7.14822C1.01855 8.27324 1.93057 9.18526 3.05559 9.18526H9.1667C10.2917 9.18526 11.2037 8.27324 11.2037 7.14822V3.07415C11.2037 1.94912 10.2917 1.03711 9.1667 1.03711Z" stroke="currentColor" stroke-width="0.763889" />
                      <path d="M2.54639 3.5835L4.12267 4.93459C5.07158 5.74793 5.54603 6.15462 6.1112 6.15462C6.67638 6.15462 7.1508 5.74793 8.09971 4.93459L9.67602 3.5835" stroke="currentColor" stroke-width="0.763889" stroke-linejoin="round" />
                      <path d="M4.07416 5.11133L2.54639 6.63911" stroke="currentColor" stroke-width="0.763889" stroke-linejoin="round" />
                      <path d="M8.14795 5.11133L9.67573 6.63911" stroke="currentColor" stroke-width="0.763889" stroke-linejoin="round" />
                    </svg>
                    <div className='overlaybox-tooltip'>
                      <p>Send Email</p>
                    </div>
                  </i>
                </button>
              </li>
              <li>
                <button onClick={() => openActivity('schedule')}>
                  <i className='icon'>
                    <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.07422 1.01904V2.54682" stroke="currentColor" stroke-width="0.763889" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M7.14893 1.01904V2.54682" stroke="currentColor" stroke-width="0.763889" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M0.783203 4.62939H9.44061" stroke="currentColor" stroke-width="0.763889" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M9.69499 4.32901V8.65772C9.69499 10.1855 8.9311 11.204 7.14869 11.204H3.07462C1.29221 11.204 0.52832 10.1855 0.52832 8.65772V4.32901C0.52832 2.80123 1.29221 1.78271 3.07462 1.78271H7.14869C8.9311 1.78271 9.69499 2.80123 9.69499 4.32901Z" stroke="currentColor" stroke-width="0.763889" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M5.10986 6.97705H5.11482" stroke="currentColor" stroke-width="1.14583" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M3.2251 6.97705H3.23005" stroke="currentColor" stroke-width="1.14583" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M3.2251 8.50439H3.23005" stroke="currentColor" stroke-width="1.14583" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <div className='overlaybox-tooltip'>
                      <p>Add Meeting</p>
                    </div>
                  </i>
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="sidebar-accordian">
          <Accordion allowMultipleExpanded={true} allowZeroExpanded={true} preExpanded={['a']}>
            {/* About this deal */}
            <AccordionItem uuid='a'>
              <AccordionItemHeading>
                <AccordionItemButton>
                  About {object['name']}
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <div className="accordion-content">
                  {renderAboutProperties()}
                </div>
              </AccordionItemPanel>
            </AccordionItem>

            {/* Contacts */}
            <AccordionItem uuid='b'>
              <AccordionItemHeading>
                <AccordionItemButton>
                  Contacts {(singlePageData?.['contacts'] && singlePageData['contacts']['length'] > 0) ? `(${singlePageData['contacts'].length})` : ''}
                  <div className='right-siede'>
                    <button className="add-contanct" onClick={() => setRelationsModal(true, 'contact')}>+ Add Contact</button>
                  </div>
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <div className="accordion-content">
                  {singlePageData?.['contacts'] && singlePageData['contacts']['length'] > 0 ? singlePageData['contacts'].map(contact => {
                    return (
                      <div className="contact-box" key={contact['_id']}>
                        <div
                          className="contact-left"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            console.log('Navigating to contact:', contact['_id']);
                            window.location.href = `/${currentWorkspace?._id}/contacts/${contact['_id']}`;
                          }}
                        >
                          <h5>{contact?.['firstName'] ? contact['firstName'] + ' ' + contact['lastName'] : '-'}</h5>
                          <h5>{contact?.['email'] ? contact['email'] : '-'}</h5>
                          <h5>{contact?.['phone'] ? contact['phone'] : '-'}</h5>
                        </div>
                        <div className="contact-right">
                          <Dropdown isOpen={dropdowns[`contact_${contact['_id']}`] || false} toggle={() => toggleDropdownAction(`contact_${contact['_id']}`)} >
                            <DropdownToggle caret>
                              <svg width="13" height="3" viewBox="0 0 13 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6.31 0C5.59 0 5 0.59 5 1.31C5 2.03 5.59 2.62 6.31 2.62C7.03 2.62 7.62 2.03 7.62 1.31C7.62 0.59 7.03 0 6.31 0Z" fill="currentColor" />
                                <path d="M1.31 0C0.59 0 0 0.59 0 1.31C0 2.03 0.59 2.62 1.31 2.62C2.03 2.62 2.62 2.03 2.62 1.31C2.62 0.59 2.03 0 1.31 0Z" fill="currentColor" />
                                <path d="M11.31 0C10.59 0 10 0.59 10 1.31C10 2.03 10.59 2.62 11.31 2.62C12.03 2.62 12.62 2.03 12.62 1.31C12.62 0.59 12.03 0 11.31 0Z" fill="currentColor" />
                              </svg>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => handleRemoveRelation(contact['_id'], 'contact')}> Remove Relation </DropdownItem>
                              {/* <DropdownItem>Delete</DropdownItem> */}
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                      </div>
                    )
                  }) :
                    <div className="company-box">
                      <p>Add Contacts associated with this record.</p>
                      <div className="btn-area">
                        <button className="btn-style-border" onClick={() => setRelationsModal(true, 'contact')}>Add Contact</button>
                      </div>
                    </div>
                  }
                </div>
              </AccordionItemPanel>
            </AccordionItem>

            {/* Companies */}
            <AccordionItem uuid='c'>
              <AccordionItemHeading>
                <AccordionItemButton>
                  Companies {(singlePageData?.['companies'] && singlePageData['companies']['length'] > 0) ? `(${singlePageData['companies'].length})` : ''}
                  <div className='right-siede'>
                    <button className="add-contanct" onClick={() => setRelationsModal(true, 'company')}>+ Add Company</button>
                  </div>
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <div className="accordion-content">
                  {singlePageData?.['companies'] && singlePageData['companies']['length'] > 0 ? singlePageData['companies'].map(company => {
                    return (
                      <div className="contact-box" key={company['_id']}>
                        <div
                          className="contact-left"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            console.log('Navigating to company:', company['_id']);
                            window.location.href = `/${currentWorkspace?._id}/companies/${company['_id']}`;
                          }}
                        >
                          <h5>{company?.['name'] || '-'}</h5>
                          <h5>{company?.['industry'] || '-'}</h5>
                          <h5>{company?.['website'] || '-'}</h5>
                          <h5>{company?.['phone'] || '-'}</h5>
                        </div>
                        <div className="contact-right">
                          <Dropdown isOpen={dropdowns[`company_${company['_id']}`] || false} toggle={() => toggleDropdownAction(`company_${company['_id']}`)} >
                            <DropdownToggle caret>
                              <svg width="13" height="3" viewBox="0 0 13 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6.31 0C5.59 0 5 0.59 5 1.31C5 2.03 5.59 2.62 6.31 2.62C7.03 2.62 7.62 2.03 7.62 1.31C7.62 0.59 7.03 0 6.31 0Z" fill="currentColor" />
                                <path d="M1.31 0C0.59 0 0 0.59 0 1.31C0 2.03 0.59 2.62 1.31 2.62C2.03 2.62 2.62 2.03 2.62 1.31C2.62 0.59 2.03 0 1.31 0Z" fill="currentColor" />
                                <path d="M11.31 0C10.59 0 10 0.59 10 1.31C10 2.03 10.59 2.62 11.31 2.62C12.03 2.62 12.62 2.03 12.62 1.31C12.62 0.59 12.03 0 11.31 0Z" fill="currentColor" />
                              </svg>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => handleRemoveRelation(company['_id'], 'company')}> Remove Relation </DropdownItem>
                              {/* <DropdownItem>Delete</DropdownItem> */}
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                      </div>
                    )
                  }) :
                    <div className="company-box">
                      <p>Add Companies associated with this record.</p>
                      <div className="btn-area">
                        <button className="btn-style-border" onClick={() => setRelationsModal(true, 'company')}>Add Company</button>
                      </div>
                    </div>
                  }
                </div>
              </AccordionItemPanel>
            </AccordionItem>

            {/* Deals */}
            <AccordionItem uuid='d'>
              <AccordionItemHeading>
                <AccordionItemButton>
                  Deals {(singlePageData?.['deals'] && singlePageData['deals']['length'] > 0) ? `(${singlePageData['deals'].length})` : ''}
                  <div className='right-siede'>
                    <button className="add-contanct" onClick={() => setRelationsModal(true, 'deal')}>+ Add Deal</button>
                  </div>
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <div className="accordion-content">
                  {singlePageData?.['deals'] && singlePageData['deals']['length'] > 0 ? singlePageData['deals'].map(deal => {
                    return (
                      <div className="contact-box" key={deal['_id']}>
                        <div
                          className="contact-left"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            console.log('Navigating to deal:', deal['_id']);
                            window.location.href = `/${currentWorkspace?._id}/deals/${deal['_id']}`;
                          }}
                        >
                          <h5>{deal?.['title'] || '-'}</h5>
                          <h5>Amount: ${deal?.['amount'] ? deal['amount'].toLocaleString() : '0'}</h5>
                          <h5>Stage: {deal?.['stage'] || '-'}</h5>
                          <h5>Close Date: {deal?.['closeDate'] ? new Date(deal['closeDate']).toLocaleDateString() : '-'}</h5>
                        </div>
                        <div className="contact-right">
                          <Dropdown isOpen={dropdowns[`deal_${deal['_id']}`] || false} toggle={() => toggleDropdownAction(`deal_${deal['_id']}`)} >
                            <DropdownToggle caret>
                              <svg width="13" height="3" viewBox="0 0 13 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6.31 0C5.59 0 5 0.59 5 1.31C5 2.03 5.59 2.62 6.31 2.62C7.03 2.62 7.62 2.03 7.62 1.31C7.62 0.59 7.03 0 6.31 0Z" fill="currentColor" />
                                <path d="M1.31 0C0.59 0 0 0.59 0 1.31C0 2.03 0.59 2.62 1.31 2.62C2.03 2.62 2.62 2.03 2.62 1.31C2.62 0.59 2.03 0 1.31 0Z" fill="currentColor" />
                                <path d="M11.31 0C10.59 0 10 0.59 10 1.31C10 2.03 10.59 2.62 11.31 2.62C12.03 2.62 12.62 2.03 12.62 1.31C12.62 0.59 12.03 0 11.31 0Z" fill="currentColor" />
                              </svg>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => handleRemoveRelation(deal['_id'], 'deal')}> Remove Relation </DropdownItem>
                              {/* <DropdownItem>Delete</DropdownItem> */}
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                      </div>
                    )
                  }) : (
                    <div className="company-box">
                      <p>Add Deals associated with this record.</p>
                      <div className="btn-area">
                        <button className="btn-style-border" onClick={() => setRelationsModal(true, 'deal')}>Add Deal</button>
                      </div>
                    </div>
                  )}
                </div>
              </AccordionItemPanel>
            </AccordionItem>

            {/* Attachments */}
            <AccordionItem uuid='attachments'>
              <AccordionItemHeading>
                <AccordionItemButton>
                  Attachments
                  <div className='right-siede'>
                    <span className="tag">{objectAttachments?.length || 0}</span>
                  </div>
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <div className="accordion-content">
                  <AttachmentList
                    attachments={objectAttachments || []}
                    showUploadedBy={true}
                    enableActions={true}
                    onDelete={handleAttachmentDelete}
                  />
                  <div className="btn-area">
                    <button className="btn-style-border" onClick={() => setAttachmentModalOpen(true)}>
                      <i className="fa fa-paperclip mr-1" /> Upload Files
                    </button>
                  </div>
                </div>
              </AccordionItemPanel>
            </AccordionItem>

            {/* Subscriptions */}
            {/* <AccordionItem uuid='e'>
            <AccordionItemHeading>
              <AccordionItemButton>
                Subscriptions
                <div className='right-siede'>
                  <button className="add-contanct">+ Add Sub</button>
                </div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              <div className="accordion-content">
                <div className="company-box">
                  <p>Add Subscriptions associated with this record. A payment is created when a customer pays or a recurring Subscriptions is processed through Sell Agent.</p>
                  <div className="btn-area">
                    <button className="btn-style-border">Set up payments</button>
                  </div>
                </div>
              </div>
            </AccordionItemPanel>
          </AccordionItem> */}

            {/* Payments */}
            {/* <AccordionItem uuid=''>
            <AccordionItemHeading>
              <AccordionItemButton>
                Payments
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              <div className="accordion-content">
                <div className="company-box">
                  <p>Track payments associated with this record. A payment is created when a customer pays or a recurring payment is processed through Sell Agent.</p>
                  <div className="btn-area">
                    <button className="btn-style-border">Set up payments</button>
                  </div>
                </div>
              </div>
            </AccordionItemPanel>
          </AccordionItem> */}

            {/* Slack */}
            {/* <AccordionItem uuid=''>
            <AccordionItemHeading>
              <AccordionItemButton>
                Slack
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              <div className="accordion-content">
                <div className="company-box">
                  <p>Collaborate with your team by associating this deal with a new or existing Slack channel.</p>
                  <div className="btn-area">
                    <button className="btn-style-border">Associate Slack channel</button>
                  </div>
                </div>
              </div>
            </AccordionItemPanel>
          </AccordionItem> */}
          </Accordion>
        </div>
      </aside>
      <PropertiesSlider
        singleId={singleId}
        isOpen={isPropertiesSlider}
        singlePageData={singlePageData}
        objectType={object['plural']?.toLowerCase()}
        onClose={() => setIsPropertiesSlider(false)}
      />
      {object['name'] === 'deal' && (
        <StageHistorySlider
          isOpen={isStageHistorySlider}
          onClose={() => setIsStageHistorySlider(false)}
          stageHistory={singlePageData?.['stageHistory']}
          pipelines={pipelines}
          currentPipelineId={singlePageData?.['pipelineId']?.['_id']}
        />
      )}

      {/* Attachment Upload Modal */}
      <Modal isOpen={attachmentModalOpen} toggle={() => setAttachmentModalOpen(false)} className={`main-modal new-user-modal modal-dialog-centered endcall-modal`}>
        <ModalHeader toggle={() => setAttachmentModalOpen(false)}>
          <h3>Upload Attachments</h3>
        </ModalHeader>
        <ModalBody>
          <div className='add-content mt-4'>
            <div className='group-from'>
              <AttachmentUploader
                onFilesAdded={handleFilesAdded}
                existingFiles={pendingFiles}
                maxFiles={10}
                maxSize={100 * 1024 * 1024}
                showByDefault={true}
              />
            </div>

            <div className='group-from btn-group mt-4'>
              <button className='btn-style-new' onClick={handleUploadFiles} disabled={pendingFiles.length === 0}>Upload {pendingFiles.length > 0 && `(${pendingFiles.length})`}</button>
              <button
                className='btn-style-border'
                onClick={() => { setAttachmentModalOpen(false); setPendingFiles([]); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Delete Object Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={() => setIsDeleteModalOpen(false)} keyboard={true} className={`main-modal modal-dialog-centered new-user-modal`}>
        <ModalHeader toggle={() => setIsDeleteModalOpen(false)}></ModalHeader>
        <ModalBody>
          <div className='content-area'>
            <div className='delete-modal'>
              <h4>
                <i className='icon'>
                  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z' fill='#FF3B30' fillOpacity='0.15' />
                    <path d='M12 19.5C16.1421 19.5 19.5 16.1421 19.5 12C19.5 7.85786 16.1421 4.5 12 4.5C7.85786 4.5 4.5 7.85786 4.5 12C4.5 16.1421 7.85786 19.5 12 19.5Z' fill='#FF3B30' />
                    <path d='M12 9V12' stroke='#FFE2E0' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    <path d='M12 15H12.0083' stroke='#FFE2E0' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                </i>
                Delete {object['label'] || object['name']}
              </h4>
              <p>
                Are you sure you want to delete <b>1 {object['label'] || object['name']}</b> ? You can't undo this action afterwards.
              </p>
              <div className='form-area'>
                <div className='btns-box text-right'>
                  <button className='btn-style-border' onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
                  <button className='btn-style-new' onClick={handleConfirmDelete}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default LeftSidebar;